/**
 * Application state management
 */

import { MAX_ALERTS, MAX_UNLOCATED_ALERTS, STORAGE_KEYS, FILTER_RADIUS_KM_DEFAULT } from './config.js';
import { getDistanceKm } from './utils.js';

// User location
export let userLocation = null;

// Alerts storage
export const alerts = new Map();
export const unlocatedAlerts = new Map();
export const markers = new Map();

// Distance cache - keyed by alert ID, cleared when user location changes
const distanceCache = new Map();

// Route distance cache - keyed by alert ID, stores {distance_km, duration_min}
const routeDistanceCache = new Map();

// Active filters
export const activeFilters = new Set(['brandweer', 'ambulance', 'politie', 'lifeliner', 'knrm', 'overig']);

// Current closest distance (for new alert notifications)
export let currentClosestDistance = Infinity;

// Active bursts (burst id -> burst object). Keyed by id, not scope: the map
// display set spans 24h, so one city scope can carry several sequential
// bursts — scope-keying would collapse them into one.
export const activeBursts = new Map();

const burstKey = (b) => b.id || b.scope;

export function setActiveBursts(burstArray) {
    // Skip update if burst set hasn't changed (avoid unnecessary re-renders).
    // Compare id + count + title + live/story state + news count: the
    // LLM-generated title and linked-news badge can arrive mid-burst (after
    // detection), and a burst flipping from live to ended changes its map
    // treatment — all need a repaint.
    if (burstArray.length === activeBursts.size &&
        burstArray.every(b => activeBursts.has(burstKey(b)) &&
            activeBursts.get(burstKey(b)).current_count === b.current_count &&
            activeBursts.get(burstKey(b)).title === b.title &&
            activeBursts.get(burstKey(b)).is_active === b.is_active &&
            activeBursts.get(burstKey(b)).news_article_count === b.news_article_count)) {
        return;
    }
    activeBursts.clear();
    for (const b of burstArray) activeBursts.set(burstKey(b), b);
}

// Current carousel state
export let currentAlertIndex = 0;
export let allDisplayedAlerts = [];

// Map state
export let hasInitialFit = false;

// Filter radius (km) — persisted in localStorage
export let filterRadiusKm = parseInt(localStorage.getItem(STORAGE_KEYS.filterRadius), 10) || FILTER_RADIUS_KM_DEFAULT;


// =============================================================================
// Distance Caching
// =============================================================================

/**
 * Get cached distance for an alert, or calculate and cache it
 * @param {Object} alert - Alert object with latitude/longitude
 * @returns {number|undefined} - Distance in km, or undefined if no location
 */
export function getCachedDistance(alert) {
    if (!userLocation || !alert.latitude || !alert.longitude) {
        return undefined;
    }

    // Check cache first
    if (distanceCache.has(alert.id)) {
        return distanceCache.get(alert.id);
    }

    // Calculate and cache
    const distance = getDistanceKm(
        userLocation.lat,
        userLocation.lon,
        alert.latitude,
        alert.longitude
    );
    distanceCache.set(alert.id, distance);
    return distance;
}

/**
 * Clear the distance cache (call when user location changes)
 */
export function clearDistanceCache() {
    distanceCache.clear();
    routeDistanceCache.clear();
}

/**
 * Get cached route distance for an alert
 * @param {string} alertId
 * @returns {Object|undefined} - {distance_km, duration_min} or undefined
 */
export function getCachedRouteDistance(alertId) {
    return routeDistanceCache.get(alertId);
}

/**
 * Set cached route distance for an alert
 * @param {string} alertId
 * @param {number} distanceKm
 * @param {number} durationMin
 */
export function setCachedRouteDistance(alertId, distanceKm, durationMin) {
    routeDistanceCache.set(alertId, { distance_km: distanceKm, duration_min: durationMin });
}

// =============================================================================
// State setters (to allow modification from other modules)
// =============================================================================

export function setUserLocation(location) {
    // Clear distance cache when location changes
    if (location !== userLocation) {
        clearDistanceCache();
    }
    userLocation = location;
}

export function setCurrentClosestDistance(distance) {
    currentClosestDistance = distance;
}

export function setCurrentAlertIndex(index) {
    currentAlertIndex = index;
}

export function setAllDisplayedAlerts(alertsList) {
    allDisplayedAlerts = alertsList;
}

export function setHasInitialFit(value) {
    hasInitialFit = value;
}

export function setFilterRadiusKm(km) {
    filterRadiusKm = km;
    localStorage.setItem(STORAGE_KEYS.filterRadius, String(km));
}


// Alert management

export function addLocatedAlert(alert) {
    alerts.set(alert.id, alert);

    // Limit stored alerts
    if (alerts.size > MAX_ALERTS) {
        const oldest = alerts.keys().next().value;
        alerts.delete(oldest);
        return oldest; // Return deleted ID for marker cleanup
    }
    return null;
}

export function addUnlocatedAlert(alert) {
    unlocatedAlerts.set(alert.id, alert);

    // Limit unlocated alerts
    if (unlocatedAlerts.size > MAX_UNLOCATED_ALERTS) {
        const oldest = unlocatedAlerts.keys().next().value;
        unlocatedAlerts.delete(oldest);
    }
}


// Location persistence

export function saveManualLocation(location) {
    localStorage.setItem(STORAGE_KEYS.manualLocation, JSON.stringify(location));
}

export function loadManualLocation() {
    const saved = localStorage.getItem(STORAGE_KEYS.manualLocation);
    return saved ? JSON.parse(saved) : null;
}

export function clearManualLocation() {
    localStorage.removeItem(STORAGE_KEYS.manualLocation);
}
